import { useLayoutEffect, useRef, useSyncExternalStore, type ReactElement } from "react";
import { say } from "../../words.ts";
import { ReadingPanel } from "../../ui/kit/index.ts";
import { PAGE_KEYS } from "../../ui/kit/reading-pages.ts";
import type { InputRouter } from "../../ui/input/router.ts";
import { pageKeys, type KeyTable } from "../controls/page.ts";
import type { Reading, ReadingSource } from "./store.ts";

/**
 * The game page's reading panel (P1.9-T37, ADR-061's amendment "the reading panel", ADR-077): the kit's `ReadingPanel`
 * over the reading store, sharing the keyboard with the world behind it through the input router.
 *
 * - While it is open it is a layer of the router, not a hold, so the game keeps the keyboard and movement keys still
 *   move. Escape closes it as it closes any topmost layer, and while it is topmost and no text field has focus E
 *   closes it, Enter presses its focused button (Close as it opens) or closes it with none of its buttons focused, and
 *   Page Down, Page Up, Home and End turn its pages or scroll its text; the router consumes each with its auto-repeats,
 *   so the E sends no read and the Enter opens no chat input. In the chat input E and Enter type and send, and Escape
 *   leaves the input, whose layer is above this one, first.
 * - As it opens with the game holding the keys it focuses its Close button, so a screen reader announces the dialog
 *   with its text and Enter presses the button too; opened while a text field or a hold has the keyboard, it leaves
 *   focus where it is. As it closes, focus that is still inside it goes back to what had it as it opened, or leaves
 *   for the page when that was nothing to return to, which gives the keys to the game.
 * - A second reading replaces the text in the same panel, from its top or its first page, keeping its layer and its
 *   focus.
 */

/**
 * The panel's copy: the name a screen reader gives the dialog, its buttons' labels and the page counter's line (ADR-077).
 * Drafts for the author, shown at `/ui?page=reading`.
 */
export const READING_WORDING = {
  label: say("reading.label"),
  close: say("reading.close"),
  back: say("reading.back"),
  next: say("reading.next"),
  /**
   * The page counter's line.
   *
   * @param page The page shown, from 1.
   * @param count The number of pages.
   * @returns The line.
   */
  pageOf: (page: number, count: number): string => say("reading.pageOf", { page, count }),
} as const;

/**
 * The keys beside Escape that close the panel while it is the router's topmost layer and no text field has focus: the
 * key the table binds Interact to (E by default; P1.9-T50, `controls/`), read as the panel opens, which is never
 * while Settings is open. Tab is never one of them: the panel opens with focus on its button, and Tab moves through
 * its buttons whichever action it is bound to; nor is a key the panel keeps for itself, Enter and its page keys
 * (P1.9-T174, point 9: a layer's own key wins over an action bound to it).
 *
 * @param keys The key table; the page's unless given.
 * @returns The keys.
 */
export function readingCloseKeys(keys: Pick<KeyTable, "code"> = pageKeys): readonly string[] {
  const code = keys.code("interact");
  return code === "Tab" || code === "Enter" || code === "NumpadEnter" || (PAGE_KEYS as readonly string[]).includes(code) ? [] : [code];
}

/**
 * Enter presses the panel's focused button, which is Close as the panel opens, and closes the panel when none of its
 * buttons has focus (ADR-077 decision 4), so Enter still accepts and a focused Next still turns the page.
 *
 * @param panel The dialog's element.
 * @param close Closes the panel.
 */
function pressEnter(panel: HTMLElement | null, close: () => void): void {
  const focused = document.activeElement;
  if (panel !== null && focused instanceof HTMLButtonElement && panel.contains(focused)) {
    focused.click();
    return;
  }
  close();
}

/**
 * What the panel needs from the store: the reading to show, and the way to close it.
 */
export interface ReadingControl extends ReadingSource {
  /**
   * Closes the panel, if it is open.
   */
  close(): void;
}

/**
 * The panel, shown while the store holds a reading.
 *
 * @param props The `store`, and the `input` router it opens its layer on and reads the keyboard's owner from.
 * @returns The panel, or nothing while closed.
 */
export function ReadingView({ store, input }: { readonly store: ReadingControl; readonly input: Pick<InputRouter, "open" | "owner"> }): ReactElement | null {
  const reading = useSyncExternalStore(store.subscribe, store.getSnapshot);
  return reading === null ? null : <OpenReading reading={reading} store={store} input={input} />;
}

/**
 * The panel while open: its layer, its focus and its text.
 *
 * @param props The `reading` shown, the `store` and the `input` router.
 * @returns The panel.
 */
function OpenReading({ reading, store, input }: { readonly reading: Reading; readonly store: ReadingControl; readonly input: Pick<InputRouter, "open" | "owner"> }): ReactElement {
  const panel = useRef<HTMLDivElement>(null);
  const navigate = useRef<((key: string) => void) | null>(null);

  useLayoutEffect(() => {
    const opener = document.activeElement;
    const pageKeys = Object.fromEntries(PAGE_KEYS.map((key) => [key, () => navigate.current?.(key)]));
    const layer = input.open({ onClose: () => store.close(), closeKeys: readingCloseKeys(), keys: { ...pageKeys, Enter: () => pressEnter(panel.current, () => store.close()) } });
    if (input.owner === "game") {
      panel.current?.querySelector<HTMLElement>("[data-kit='reading-actions'] > .ot-button--primary")?.focus();
    }
    return () => {
      const box = panel.current;
      if (box !== null && box.contains(document.activeElement)) {
        if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected && !box.contains(opener)) {
          opener.focus();
        } else {
          (document.activeElement as HTMLElement).blur();
        }
      }
      layer.close();
    };
  }, [store, input]);

  return <ReadingPanel text={reading.text} layout={reading.layout} serial={reading.serial} label={READING_WORDING.label} words={READING_WORDING} onClose={() => store.close()} panel={panel} navigate={navigate} />;
}
